import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Typography,
  Switch,
  Input,
  Space,
  Modal,
  message,
  Select,
} from "antd";
import {
  fetchVerificationRequests,
  updateVerificationStatus,
} from "../useAPIDetails/useVerificationList";
const { Option } = Select;
const { Title } = Typography;
const { TextArea } = Input;

const VerificationList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filterStatus, setFilterStatus] = useState("pending");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedProof, setSelectedProof] = useState({ front: "", back: "" });
  const [hasNextPage, setHasNextPage] = useState(true); // Track if there is a next page

  // Open Modal with Proof Images
  const handleViewClick = (record) => {
    setSelectedProof({
      front: record.proofFront,
      back: record.proofBack,
    });
    setIsModalVisible(true);
  };

  // Fetch Data with Pagination and Filtering
  const loadData = async (page = 1, size = 10, status = "pending") => {
    setLoading(true);
    try {
      let params = { page: page - 1, size };

      switch (status) {
        case "pending":
          params.approved = false;
          params.rejected = false;
          break;
        case "approved":
          params.approved = true;
          break;
        case "rejected":
          params.rejected = true;
          break;
        default:
          break;
      }

      const result = await fetchVerificationRequests(params);
      const fetchedData = result?.data?.requests?.map((item) => ({
        id: item?.id,
        fullName: `${item?.userProfile?.firstName} ${item?.userProfile?.lastName}` || "",
        userName: item?.userProfile?.userName || "-",
        govIdProof: item?.proofType,
        proofFront: item?.proofFrontView?.fileUrl,
        proofBack: item?.proofBackView?.fileUrl,
        approved: item?.approved,
        rejected: item?.rejected,
        reason: "",
      }));

      setData(fetchedData);
      setPagination({
        ...pagination,
        current: page,
        total: result?.data?.totalCount || 0,
      });

      // Set next page availability
      setHasNextPage(fetchedData.length === size); // If response length equals pageSize, there might be more data
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(pagination.current, pagination.pageSize, filterStatus);
  }, [filterStatus]);

  // Handle Table Change (Pagination)
  const handleTableChange = (pagination) => {
    loadData(pagination.current, pagination.pageSize, filterStatus);
  };

  // Handle Toggle Changes
  const handleToggleChange = (id, type) => {
    setData((prevData) =>
      prevData?.map((item) =>
        item.id === id
          ? {
              ...item,
              approved: type === "approved" ? !item.approved : false,
              rejected: type === "rejected" ? !item.rejected : false,
            }
          : item
      )
    );
  };

  // Handle Reason Change
  const handleReasonChange = (id, value) => {
    setData((prevData) =>
      prevData?.map((item) =>
        item.id === id ? { ...item, reason: value } : item
      )
    );
  };

  // Handle Save per Row
  const handleSave = async (id) => {
    const row = data.find((item) => item.id === id);
    if (!row.approved && !row.rejected) {
      message.warning(
        "Please select either Approved or Rejected before saving."
      );
      return;
    }

    setLoading(true);
    try {
      await updateVerificationStatus(
        row.id,
        row.approved,
        row.rejected,
        row.reason || null
      );
      message.success("Profile verified successfully.");
      loadData(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to verify the user."
      );
    } finally {
      setLoading(false);
    }
  };

  // Close Modal
  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedProof({ front: "", back: "" });
  };

  const formatText = (text) => {
    return text
      .toLowerCase() 
      .replace(/_/g, " ") 
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };
  // Table Columns
  const columns = [
    {
      title: "Name",
      dataIndex: "fullName",
      key: "fullName",
    },
    {
      title: "User Name",
      dataIndex: "userName",
      key: "userName",
    },
    {
      title: "Government ID Proof",
      dataIndex: "govIdProof",
      key: "govIdProof",
      render: (text) => formatText(text),
    },
    {
      title: "ID Card",
      key: "idCard",
      render: (record) => (
        <Button
          style={{
            backgroundColor: "#FF7D4D",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
          }}
          onClick={() => handleViewClick(record)}
        >
          View
        </Button>
      ),
    },
    {
      title: "Approval Status",
      key: "approvalStatus",
      render: (record) => (
        <Space direction="vertical">
          <Switch
            checked={record.approved}
            onChange={() => handleToggleChange(record.id, "approved")}
            checkedChildren="Approved"
            unCheckedChildren="Approved"
          />
          <Switch
            checked={record.rejected}
            onChange={() => handleToggleChange(record.id, "rejected")}
            checkedChildren="Rejected"
            unCheckedChildren="Rejected"
          />
        </Space>
      ),
    },
    {
      title: "Reason",
      key: "reasonSave",
      render: (record) => (
        <Space style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <TextArea
            rows={2}
            placeholder="Reason (optional)"
            value={record.reason}
            onChange={(e) => handleReasonChange(record.id, e.target.value)}
            style={{
              fontSize: "12px",
              resize: "none",
              width: "70%",
            }}
          />
          <Button
            type="primary"
            onClick={() => handleSave(record.id)}
            disabled={loading}
            style={{
              backgroundColor: "#FF7D4D",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
            }}
          >
            Save
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div
      className="flex-grow-content"
      style={{
        padding: "24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "80%",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          marginBottom: "16px",
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Profile Verification
        </Title>
        <Select
          style={{ width: 120 }}
          value={filterStatus}
          onChange={(value) => setFilterStatus(value)}
        >
          <Option value="pending">Pending</Option>
          <Option value="approved">Approved</Option>
          <Option value="rejected">Rejected</Option>
        </Select>
      </div>

      <Table
        style={{
          border: "1px solid #E1E1E1",
          borderRadius: "5px",
          width: "100%",
        }}
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: false,
          hideOnSinglePage: true,
        }}
        onChange={handleTableChange}
        rowKey="id"
        footer={() => (
          <div style={{ textAlign: "right" }}>
            <Button
              type="primary"
              disabled={!hasNextPage} // Disable Next button if no more pages
              onClick={() =>
                loadData(pagination.current + 1, pagination.pageSize, filterStatus)
              }
            >
              Next
            </Button>
          </div>
        )}
      />

      <Modal
        title="ID Card"
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        centered
        style={{ maxWidth: "600px", margin: "auto" }} // Optional styling for centered modal width
        bodyStyle={{
          maxHeight: "65vh",
          overflowY: "auto",
          scrollbarWidth: "thin",
        }}
        afterOpenChange={(open) => {
          if (open) {
            document.body.style.overflow = "hidden"; // Disable background scrolling
          } else {
            document.body.style.overflow = ""; // Re-enable background scrolling
          }
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "32px",
            padding: "16px",
          }}
        >
          {selectedProof.front && (
            <img
              src={selectedProof.front}
              alt="Front Proof"
              style={{
                width: "100%",
                borderRadius: "8px",
                overflow: "hidden",
                border: "3px solid #E1E1E1",
              }}
            />
          )}
          {selectedProof.back && (
            <img
              src={selectedProof.back}
              alt="Back Proof"
              style={{
                width: "100%",
                borderRadius: "8px",
                overflow: "hidden",
                border: "3px solid #E1E1E1",
              }}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default VerificationList;
